
// Refactored for readability/maintainability and with comments for complex logic.
// See custom hook in useSelectTimeState.ts for state and navigation logic.

import React, { useEffect } from "react";
import { useTranslation } from "react-i18next";
import SelectTimeContainer from "@features/booking/components/SelectTimeContainer";
import BookingProgressFooter from "@features/booking/components/BookingProgressFooter";
import SelectTimeMain from "@features/booking/components/SelectTimeMain";
import ErrorBoundary from "@shared/components/common/ErrorBoundary";
import { useSelectTimeState } from "@features/booking";
import { useBookingFlow } from "@features/booking/flow";
import { useAnalytics, ANALYTICS_EVENTS } from "@core/analytics";
import { useRefreshAuthProfile } from "@core/auth";
import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogHeader,
  AlertDialogFooter,
  AlertDialogTitle,
  AlertDialogDescription,
  AlertDialogAction,
} from "@shared/components/ui/alert-dialog";
import type { LimitWarningType } from "@features/booking/hooks/useBookingFlow";

/**
 * SelectTime Page (Stepper)
 * Handles the entire "Pick a time" flow.
 * - All business logic is isolated in the `useSelectTimeState` hook.
 * - This component is now a lightweight orchestrator.
 */
const SelectTime = () => {
  const { progress } = useBookingFlow();
  const { t } = useTranslation('booking');
  const { trackEvent } = useAnalytics();
  useRefreshAuthProfile();

  useEffect(() => {
    trackEvent({ name: ANALYTICS_EVENTS.BOOKING_SELECT_TIME });
  }, [trackEvent]);

  // Get all state, handlers, and computed values from custom hook
  const {
    showExperience,
    selectedExp,
    setSelectedExp,
    selectedTime,
    loading,
    EXPERIENCES,
    availableTimes,
    handleSelectTime,
    handleBack,
    getSelectedExperienceLabel,
    handleJoinWaitingList,
    columnType,
    allowWaitingList,
    isWalkInDay,
    isWaitingListDay,
    centerHasOnlyPreferred,
    isAuthenticated,
    handlePreferredLogin,
    limitWarning,
    closeLimitWarning,
    crossServiceAlternatives,
    handleSelectCrossServiceSlot,
    calendarAvailability,
    isCalendarLoading,
    handleCalendarDateSelect,
    showNoAvailabilityCalendar,
    calendarServiceTypes,
    currentService,
    partnerSuggestions,
    alternativeMenus,
    handleSelectAlternativeMenu,
  } = useSelectTimeState();

  return (
    <SelectTimeContainer>
      <ErrorBoundary>
        <SelectTimeMain
          showExperience={showExperience}
          EXPERIENCES={EXPERIENCES}
          selectedExp={selectedExp}
          onExperienceChange={setSelectedExp}
          getSelectedExperienceLabel={getSelectedExperienceLabel}
          availableTimes={availableTimes}
          selectedTime={selectedTime}
          handleSelectTime={handleSelectTime}
          loading={loading}
          onJoinWaitingList={handleJoinWaitingList}
          columnType={columnType}
          allowWaitingList={allowWaitingList}
          isWalkInDay={isWalkInDay}
          isWaitingListDay={isWaitingListDay}
          centerHasOnlyPreferred={centerHasOnlyPreferred}
          isAuthenticated={isAuthenticated}
          onPreferredLogin={handlePreferredLogin}
          crossServiceAlternatives={crossServiceAlternatives}
          onSelectCrossServiceSlot={handleSelectCrossServiceSlot}
          calendarAvailability={calendarAvailability}
          isCalendarLoading={isCalendarLoading}
          onCalendarDateSelect={handleCalendarDateSelect}
          showNoAvailabilityCalendar={showNoAvailabilityCalendar}
          calendarServiceTypes={calendarServiceTypes}
          currentService={currentService}
          partnerSuggestions={partnerSuggestions}
          alternativeMenus={alternativeMenus}
          onSelectAlternativeMenu={handleSelectAlternativeMenu}
        />
      </ErrorBoundary>
      <BookingProgressFooter onBack={handleBack} progress={progress} />

      <LimitWarningDialog
        warning={limitWarning}
        onClose={closeLimitWarning}
        t={t}
      />
    </SelectTimeContainer>
  );
};

function LimitWarningDialog({
  warning,
  onClose,
  t,
}: {
  warning: { open: boolean; type: LimitWarningType; limit: number; contactEmail: string } | null;
  onClose: () => void;
  t: (key: string, opts?: Record<string, unknown>) => string;
}) {
  if (!warning) return null;

  const isBlocked = warning.type === 'blocked';
  const title = isBlocked
    ? t('BLOCKED_USER_TITLE')
    : t('MODAL_WARNING_DAILY_TITLE_1');

  let subtitle = '';
  let text = '';
  if (isBlocked) {
    text = t('BLOCKED_USER_SUBTITLE');
  } else {
    subtitle = t(`MODAL_WARNING_${warning.type.toUpperCase()}_TITLE_2`, { bookings: warning.limit });
    text = t(`MODAL_WARNING_${warning.type.toUpperCase()}_TEXT_1`, { bookings: warning.limit });
  }

  return (
    <AlertDialog open={warning.open} onOpenChange={(open) => { if (!open) onClose(); }}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>{title}</AlertDialogTitle>
          <AlertDialogDescription asChild>
            <div className="space-y-2">
              {subtitle && <p className="font-medium text-foreground">{subtitle}</p>}
              <p>{text}</p>
              {!isBlocked && warning.contactEmail && (
                <a
                  href={`mailto:${warning.contactEmail}`}
                  className="block text-sm font-medium text-primary underline"
                >
                  {warning.contactEmail}
                </a>
              )}
            </div>
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogAction onClick={onClose}>
            {t('LIMIT_WARNING_CLOSE')}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}

export default SelectTime;
